import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useAuth } from '../stores/auth';

import { es } from './catalog/es';
import { resolveLocale, storeLocale } from './locale';
import type { Locale } from './locale';
import type { Node, Plural, PluralKey, TextKey, Values } from './types';

/** `es` es el catálogo canónico: su forma es el contrato que `en` debe cumplir. */
export type Catalog = typeof es;

/**
 * El español va estático; el inglés se carga cuando hace falta.
 *
 * Cada catálogo son unos veinte kilobytes comprimidos, y el presupuesto de
 * `13 §8` son 250 KB para el arranque. Meter los dos significaba pagar el
 * idioma que la mayoría de esta instalación no usa —`User::$locale` es `es`—
 * en cada carga.
 *
 * El coste es una petición extra en el arranque de quien tiene la interfaz en
 * inglés, y **solo la primera vez**: después el módulo está en la caché del
 * navegador. Mientras llega, el proveedor no pinta nada en lugar de pintar el
 * español, que sería un parpadeo de idioma en cada recarga.
 */
const LAZY: Record<string, () => Promise<{ default: Node }>> = {
  en: () => import('./catalog/en').then((module) => ({ default: module.en })),
};

export type Key = TextKey<Catalog>;
export type CountKey = PluralKey<Catalog>;

function lookup(catalog: Node, path: string): Node | undefined {
  let node: Node | undefined = catalog;

  for (const segment of path.split('.')) {
    if (node === undefined || typeof node === 'string') {
      return undefined;
    }

    node = (node as Record<string, Node>)[segment];
  }

  return node;
}

/**
 * Sustituye `{marcador}` por su valor.
 *
 * Sin marcadores no se recorre nada: la inmensa mayoría de las cadenas de la
 * interfaz son literales, y esta función se llama en cada renderizado.
 */
function interpolate(text: string, values: Values | undefined): string {
  if (values === undefined || !text.includes('{')) {
    return text;
  }

  return text.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = values[name];

    return value === undefined ? match : String(value);
  });
}

interface I18n {
  locale: Locale;
  /** Traduce una clave. Devuelve la clave si falta, que se ve y no rompe. */
  t: (key: Key, values?: Values) => string;
  /** Traduce eligiendo forma según `count`, que además se interpola como `{count}`. */
  plural: (key: CountKey, count: number, values?: Values) => string;
  setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  // El idioma vive en el usuario, no en un estado propio: así el cambio en la
  // pantalla de cuenta —que ya hace `PATCH /auth/me` y refresca el store—
  // repinta la aplicación entera sin ningún puente extra.
  const userLocale = useAuth((state) => state.user?.locale);
  const locale = resolveLocale(userLocale);

  // Los catálogos ya cargados. `es` está desde el principio porque es estático.
  const [loaded, setLoaded] = useState<Record<string, Node>>({ es });

  useEffect(() => {
    const load = LAZY[locale];

    if (load === undefined || locale in loaded) {
      return;
    }

    let cancelled = false;

    void load().then((module) => {
      if (!cancelled) {
        setLoaded((current) => ({ ...current, [locale]: module.default }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [locale, loaded]);

  // El espejo local y los atributos del documento se mantienen aquí y no en el
  // arranque, porque el idioma puede cambiar en caliente desde los ajustes.
  // `lang` no es decorativo: de él dependen el lector de pantalla, la
  // separación silábica y el corrector del navegador.
  useEffect(() => {
    storeLocale(locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const catalog = loaded[locale];

  const t = useCallback(
    (key: Key, values?: Values) => {
      const found = catalog === undefined ? undefined : lookup(catalog, key);

      return typeof found === 'string' ? interpolate(found, values) : key;
    },
    [catalog],
  );

  const plural = useCallback(
    (key: CountKey, count: number, values?: Values) => {
      const found = catalog === undefined ? undefined : lookup(catalog, key);

      if (found === undefined || typeof found === 'string') {
        return key;
      }

      const forms = found as Plural;
      // `Intl.PluralRules` y no `count === 1`: el inglés y el español coinciden
      // hoy, pero el cero tiene forma propia en varias lenguas y escribir la
      // regla a mano es la clase de atajo que se paga al añadir el tercer
      // idioma.
      const category = count === 0 && forms.zero !== undefined ? 'zero' : new Intl.PluralRules(locale).select(count);
      // `Intl.PluralRules` puede devolver `few`, `many` o `two`, categorías que
      // el español y el inglés no usan; el catálogo solo declara las que
      // existen, así que cualquier otra cae en `other`.
      const text = (forms as unknown as Record<string, string | undefined>)[category] ?? forms.other;

      return interpolate(text, { count, ...values });
    },
    [catalog, locale],
  );

  const setLocale = useCallback((next: Locale) => {
    storeLocale(next);

    // Sin sesión —login, verificación de correo, reseteo— no hay a quién
    // guardarle la preferencia, así que el espejo local es todo lo que hay y
    // hace falta forzar el repintado.
    const user = useAuth.getState().user;

    if (user) {
      useAuth.getState().setUser({ ...user, locale: next });
    } else {
      document.documentElement.lang = next;
    }
  }, []);

  const value = useMemo<I18n>(() => ({ locale, t, plural, setLocale }), [locale, t, plural, setLocale]);

  // Nada hasta tener el catálogo del idioma pedido. Solo puede ocurrir en el
  // primer arranque de una interfaz en inglés, y dura lo que un `import()`.
  if (catalog === undefined) {
    return null;
  }

  return <I18nContext value={value}>{children}</I18nContext>;
}

export function useI18n(): I18n {
  const value = use(I18nContext);

  if (value === null) {
    throw new Error('useI18n fuera de <I18nProvider>');
  }

  return value;
}

/** El atajo que usan las pantallas: `const t = useT()`. */
export function useT(): I18n['t'] {
  return useI18n().t;
}
